import React from 'react';
import { Table, Card, Tooltip } from 'antd';
import { InfoCircleOutlined } from '@ant-design/icons';
import { useTranslation, TFunction } from 'react-i18next';
import { Log } from './hooks/usePlantSensorData';
import { CustomSchema, CustomSchemaMetadata } from './hooks/useCustomSchema';
import { InternalSchemaDiagnostics } from '../components/InternalSchemaTools';

type SchemaMapping = {
  [key: string]: { rawField?: string; title: string; explainer: string };
};

// Default schema mapping for PlantModule logs
const defaultSchemaMapping: SchemaMapping = {
  Te: {
    title: 'Temperatur',
    explainer: 'Temperatur målt af sensoren i grader Celsius',
  },
  EE: {
    title: 'EE',
    explainer: 'hvor mange gange den har gemt data i eeprom',
  },
  l: {
    title: 'Liter',
    explainer: 'Vandmængde i liter',
  },
  m2: {
    rawField: 'm3 / m2',
    title: 'Kubikmeter',
    explainer: 'Vandmængde i kubikmeter (m³)',
  },
  cm: {
    title: 'Niveau (cm)',
    explainer: 'Støvbinder niveau målt i centimeter',
  },
  vo: {
    title: 'Volt',
    explainer: 'Volt power til sensoren',
  },
  pu: {
    title: 'Pumpe',
    explainer:
      'pumpe tid totalt ("0" = pumpen høre ikke pt og efter "," er tiden i timer:minutter)',
  },
  al: {
    title: 'Alarm',
    explainer:
      'alarm ("1"= der er en alarm pt og efter "," antal gange der har været en alarm  Alarm = vandmangel (kig på displayet for fejlbesked) - PLC display',
  },
  v1: {
    title: 'Ventil 1',
    explainer:
      'Ventil1 ("1"= den er aktiv ( åben )  pt og efter "," antal gange den har været i gang',
  },
  v2: {
    title: 'Ventil 2',
    explainer:
      'Ventil2 ("1"= den er aktiv (åben ) pt og efter "," antal gange den har været i gang',
  },
  ex: {
    title: 'Extern bånd signal',
    explainer:
      'Extern bånd signal ("1"= der er et signal fra kundens anlæg at båndet køre pt og efter "," antal gange den har været i gang',
  },
  on: {
    title: 'Drift mode',
    explainer:
      'Om anlæger er i "Drift mode " ("1"= Vores anlæg er i drift pt og efter "," antal gange den har været i gang',
  },
};

const ignoreKeys = ['id', 'log_count', 'createTime'];

function mapLogsToTableData(
  logs: Log[],
  mapping: SchemaMapping,
  ignoreKeys: string[] = [],
  t: TFunction<'translation', undefined>
): { columns: any[]; dataSource: any[] } {
  const columns: any[] = [
    {
      title: '',
      dataIndex: 'Field',
      key: 'Field',
      fixed: 'left' as const,
      render: (key: string) => {
        const field = mapping[key];
        const tooltipContent = [
          field?.explainer,
          field?.rawField ? `\n\nRåfelt: ${field.rawField}` : '',
          `\nNøgle: ${key}`,
        ]
          .filter(Boolean)
          .join('');

        return (
          <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
            {field?.title || key}{' '}
            <Tooltip
              title={tooltipContent}
              overlayInnerStyle={{
                whiteSpace: 'pre-wrap',
                minWidth: '300px',
              }}
            >
              <InfoCircleOutlined />
            </Tooltip>
          </div>
        );
      },
    },
  ];
  const dataSource: any[] = [];

  // Gather unique timestamps and add them as columns (limit to 10 most recent)
  const recentLogs = logs.slice(0, 10);
  const timestamps = recentLogs.map((log) => log.createTime.toISOString());

  for (const timestamp of timestamps) {
    const date = new Date(timestamp);
    columns.push({
      title: t('date.exactFormatWithYear', {
        relativeTime: date,
        dayOfWeek: date,
        dayOfMonth: date,
        month: date,
        time: date,
        year: date,
      }),
      dataIndex: timestamp,
      key: timestamp,
    });
  }

  // Gather fields and their corresponding values for each timestamp
  const orderedKeys = Object.keys(mapping)
    .filter((key) => !ignoreKeys.includes(key) && key !== 'timestamp')
    .sort((a, b) => {
      const titleA = mapping[a]?.title || a;
      const titleB = mapping[b]?.title || b;
      return titleA.localeCompare(titleB, 'da', { sensitivity: 'base' });
    });

  for (const key of orderedKeys) {
    if (mapping.hasOwnProperty(key)) {
      const row: any = { Field: key };
      for (let i = 0; i < recentLogs.length; i++) {
        const log = recentLogs[i];
        const timestamp = timestamps[i];
        row[timestamp] = log[key] ?? '-';
      }
      // Only add row if at least one log has this key
      const hasKey = recentLogs.some((log) => log[key] !== null && log[key] !== undefined);
      if (hasKey) {
        dataSource.push(row);
      }
    }
  }

  return { columns, dataSource };
}

interface PlantModuleSchemaTableProps {
  logs: Log[];
  customSchema: CustomSchema;
  metadata: CustomSchemaMetadata;
}

export function PlantModuleSchemaTable({
  logs,
  customSchema,
  metadata,
}: PlantModuleSchemaTableProps) {
  const { t } = useTranslation();

  // Debug: Log the incoming data
  console.log('[PlantModuleSchemaTable] logs:', logs?.length, logs?.[0]);

  // Filter out _metadata from customSchema
  const { _metadata, ...customSchemaFields } = customSchema;

  // Merge default schema with custom schema (or use only custom if toggle is on)
  const mergedSchema = metadata.useOnlyCustomSchema
    ? (customSchemaFields as SchemaMapping)
    : { ...defaultSchemaMapping, ...(customSchemaFields as SchemaMapping) };

  const { columns, dataSource } = mapLogsToTableData(logs, mergedSchema, ignoreKeys, t);

  console.log(
    '[PlantModuleSchemaTable] dataSource:',
    dataSource?.length,
    'columns:',
    columns?.length
  );

  const missingKeysFromLog =
    logs.length > 0
      ? Object.keys(logs[0])
          .filter((key) => !mergedSchema[key])
          .filter((key) => !ignoreKeys.includes(key))
          .sort((a, b) =>
            a.localeCompare(b, 'da', {
              sensitivity: 'base',
              numeric: true,
            })
          )
      : [];

  return (
    <Card>
      <Table
        dataSource={dataSource}
        columns={columns}
        pagination={false}
        scroll={{ x: 'max-content' }}
        size="small"
      />
      <InternalSchemaDiagnostics
        missingKeys={missingKeysFromLog}
        title="Logs som mangler forklaring:"
        className="mt-4 text-xs text-muted-foreground"
        hideWhenEmpty
      />
    </Card>
  );
}
